import { withDefault } from "collection-utils";
import { asNumber, asString, loadedDefinedMap } from "common/components/component-helpers";
import type { PotentialValue, Value } from "common/components/types";
import { ActionSource, LOADING } from "common/components/types";
import type { GlideFunctionalRendererProps } from "common/components/uis";
import type { TextEntrySize } from "common/generator/components/text-entry-types";
import { dontAwait, parseNumberDiligently } from "common/Support";
import { useEditComponentEnabledViaNetworkStatus } from "hooks/use-edit-components-enabled-via-network-status";
import { useForce } from "hooks/use-force";
import { useOnClickFromAction } from "hooks/use-on-click-from-action";
import { useForceValueProducers } from "hooks/use-value-producers";
import TextField from "../ui/smart/text-field/text-field";
import type { FieldType } from "../ui/views/text-field/text-field-view-types";

interface Props extends GlideFunctionalRendererProps {
    readonly dataType: FieldType;

    readonly convertIn: (input: Value) => string;

    readonly convertOut?: (output: string) => Value;

    readonly getErrorMessage?: (
        input: string,
        isRequired: boolean,
        args: readonly PotentialValue[]
    ) => string | undefined;

    readonly getPrefixSuffix?: (args: readonly PotentialValue[]) => [string | undefined, string | undefined];
}

export const BaseFieldMaterialRenderer: React.FunctionComponent<Props> = p => {
    const { env, getValueProducers, ctx, dataType, convertIn, convertOut, getErrorMessage, getPrefixSuffix } = p;
    const [force] = useForce();
    const [args, [, textValueProducer, , , , , validFlagValueProducer]] = useForceValueProducers(
        getValueProducers,
        force,
        ctx
    );
    const [editingAllowed] = useEditComponentEnabledViaNetworkStatus(env);

    const [placeholder, value, title, onChangeValue, isEnabledValue, isRequiredValue, , , maximumChars, sizeValue] =
        args;
    const { onClick: onChangeCB } = useOnClickFromAction(onChangeValue, ActionSource.DateTimeField, ctx, env);

    if (value === LOADING) return env.renderProgress();

    const convertOutSane = convertOut ?? (output => withDefault<Value>(parseNumberDiligently(output), output));
    const getErrorMessageSane = getErrorMessage ?? (() => undefined);

    const onChange = async (newValue: string) => {
        if (textValueProducer === undefined) return;

        textValueProducer.set(convertOutSane(newValue));
        if (onChangeCB !== undefined) {
            dontAwait(onChangeCB(undefined));
        }
    };

    // NOTE: This is only valid for fields which don't handle require via placeholder.
    // At this point the only ones who don't are the date and date/time fields.
    // FIXME: actually use this
    const isRequired = isRequiredValue === true;
    const isEnabled = editingAllowed && (onChangeCB === undefined || isEnabledValue !== false);

    const stringValue = convertIn(value);
    const max = loadedDefinedMap(maximumChars, asNumber);
    const size = loadedDefinedMap(sizeValue, asString) as TextEntrySize | undefined;

    if (validFlagValueProducer !== undefined) {
        const errorMessage = getErrorMessageSane(stringValue, isRequired, args);

        const isOverMax = max !== undefined && stringValue.length > max;

        validFlagValueProducer.set(!isOverMax && errorMessage === undefined);
    }

    const [prefix, suffix] = getPrefixSuffix?.(args) ?? [];

    return (
        <TextField
            placeholder={placeholder === LOADING ? "Loading…" : asString(placeholder)}
            title={title === LOADING ? "Loading…" : asString(title)}
            value={stringValue}
            dataType={dataType}
            onChange={onChange}
            isEnabled={isEnabled}
            isRequired={isRequired}
            maximumChars={max}
            size={size}
            validate={s => getErrorMessageSane(s, isRequired, args)}
            prefix={prefix}
            suffix={suffix}
            appKind={env.appEnvironment.appKind}
        />
    );
};
